<template>
  <div class="widget w-bubble-chart-widget" :class="['font-size-' + fontSize, { bold: isFontBold }]">
    <c-widget-header hideLinkModeReceive printView :symbolsToWatchlist="selectedSymbols" :wid="wid">
      <slot name="buttons" slot="buttons" />
    </c-widget-header>

    <c-widget-control-bar>
      <v-form-group :label="i18n('controlBar.indexWatchlist')" label-cols="auto">
        <v-form-select
          :value="controlIndexWatchlist"
          :options="optionsIndexWatchlist"
          @input="onIndexWatchlistSelected"
          :aria-label="i18n('controlBar.indexWatchlist')"
        />
      </v-form-group>
      <v-form-group :label="i18n('controlBar.xAxis')" label-cols="auto">
        <v-form-select
          :value="controlXAxis"
          :options="optionsAxi"
          @input="onXAxisSelected"
          :aria-label="i18n('controlBar.xAxis')"
        />
      </v-form-group>
      <v-form-group :label="i18n('controlBar.yAxis')" label-cols="auto">
        <v-form-select
          :value="controlYAxis"
          :options="optionsAxi"
          @input="onYAxisSelected"
          :aria-label="i18n('controlBar.yAxis')"
        />
      </v-form-group>
      <v-form-group :label="i18n('controlBar.bubbleSize')" label-cols="auto">
        <v-form-select
          :value="controlBubbleSize"
          :options="optionsBubbleSize"
          @input="onBubbleSizeSelected"
          :aria-label="i18n('controlBar.bubbleSize')"
        />
      </v-form-group>
      <v-form-group :label="i18n('controlBar.bubbleColor')" label-cols="auto">
        <v-form-select
          :value="controlBubbleColor"
          :options="optionsBubbleColor"
          @input="onBubbleColorSelected"
          :aria-label="i18n('controlBar.bubbleColor')"
        />
      </v-form-group>
    </c-widget-control-bar>

    <div class="widget-body" v-resize:debounce.50="onResize">
      <c-loader v-if="isLoading" />
      <v-alert show v-if="isError" variant="red" align="center">{{ i18n('error', 'globalMessages') }}</v-alert>
      <highcharts
        v-show="!isLoading && !isError"
        class="chart v-stock-chart dark"
        :class="{ multiseries: controlBubbleColor !== '' }"
        :options="chartOptions"
        :updateArgs="[true, true, 700]"
        ref="chart"
      />
    </div>
  </div>
</template>

<script>
import CLoader from '@custom/CLoader';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import { API } from '@/state/api';
import Highcharts from 'highcharts';
import HighchartsMap from 'highcharts/modules/map';
import HighChartsAccessibility from 'highcharts/modules/accessibility';

import resize from 'vue-resize-directive';
import widgetCommons from '@mixins/widgetCommons';
import { debounce } from 'lodash-es';
import { getConsolidatedRic } from '@/utils/helpers';

HighchartsMap(Highcharts);
HighChartsAccessibility(Highcharts);

export default {
  name: 'w-bubble-chart-widget',
  mixins: [widgetCommons],
  components: { CLoader, CWidgetControlBar, CWidgetHeader },
  directives: {
    resize
  },
  props: {
    wid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      rangeDescription: '',
      chartOptions: {
        lang: {
          accessibility: {
            screenReaderSection: {
              beforeRegionLabel: undefined
            },
            chartContainerLabel: this.getChartTitle(),
            svgContainerLabel: this.getChartTitle(),
            defaultChartTitle: this.getChartTitle()
          }
        },
        chart: {
          type: 'bubble',
          animation: true,
          styledMode: true,
          events: {
            selection: undefined,
            click: undefined
          },
          spacing: [15, 10, 4, 3],
          zoomType: 'xy',
          resetZoomEnabled: true
        },
        accessibility: {
          enabled: true,
          keyboardNavigation: {
            enabled: true,
            seriesNavigation: {
              mode: 'normal'
            }
          },
          series: { pointDescriptionEnabledThreshold: false },
          point: {
            descriptionFormatter: undefined
          },
          screenReaderSection: {}
        },
        title: {
          text: ''
        },
        legend: {
          enabled: false,
          itemMarginBottom: 5,
          margin: -2
        },
        tooltip: {
          distance: 30,
          headerFormat: '',
          hideDelay: 75,
          pointFormatter: undefined,
          useHTML: true
        },
        xAxis: {
          events: {
            afterSetExtremes(ev) {
              if (ev.userMin) {
                if (!this.chart.resetZoomButton) this.chart.showResetZoom();
              } else if (this.chart.resetZoomButton) {
                this.chart.resetZoomButton.destroy();
                delete this.chart.resetZoomButton;
              }
            }
          },
          labels: {},
          plotLines: [{ value: 0, width: 1, zIndex: 2 }],
          tickWidth: 0,
          title: {
            text: '',
            margin: 4
          },
          accessibility: {
            enabled: true,
            rangeDescription: undefined
          }
        },
        yAxis: {
          labels: {
            x: -11
          },
          plotLines: [{ value: 0, width: 1, zIndex: 2 }],
          title: {
            text: '',
            margin: 5
          },
          accessibility: {
            enabled: true,
            rangeDescription: undefined
          }
        },
        plotOptions: {
          bubble: {
            minSize: 5,
            maxSize: 60
          },
          series: {
            allowPointSelect: true,
            dataLabels: {
              enabled: true,
              padding: 0,
              format: '{point.label}'
            },
            point: {
              events: {
                select: undefined,
                unselect: undefined
              }
            },
            stickyTracking: false
          }
        },
        credits: {
          enabled: false
        },
        series: [],
        mapNavigation: {
          enableMouseWheelZoom: true
        }
      },
      currentData: [],
      fidFormatters: {
        'calc.RANK': 'int',
        'calc.YRHIGH_DIFF': 'float.percentage.change',
        'calc.YRLOW_DIFF': 'float.percentage.change',
        'calc.REL_VOL_INTRADAY': 'float.percentage.change',
        'calc.REL_VOL_EOD': 'float.percentage.change',
        'calc.PCTCHNG_OPEN': 'float.percentage.change',
        'calc.OPEN_GAP': 'float.percentage.change',
        'calc.TARGETPRICE_DIFF': 'float.percentage.change',
        'x.YIELD': 'float.percentage.change',
        'd._BETA_1Y': 'float.percentage.change'
      },
      isError: false,
      isLoading: true,
      maxChangeColorGroups: 10,
      portfolioMap: {
        FIDELITY_PEERS: ['FIS.N', 'SCHW.N', 'AMP.N', 'ETFC.O', 'STT.N', 'BLK.N'],
        FIDELITY_PORTFOLIO: ['MMM.N', 'AAPL.O', 'FIS.N', 'IBM.N', 'JPM.N', 'PFE.N'],
        THYSSEN_KRUPP_EMS: ['TKAG.DE', 'IMI.PS', 'FLEX.F', 'CLS.TO', 'TXN.OQ', 'STM.PA', 'NXPI.O']
      },
      resizeTimeout: 0,
      sectorsMap: null,
      selectedBubbles: {}
    };
  },
  computed: {
    locale() {
      return this.$store.state.applicationSettings.general.settings.applicationLanguage;
    },
    optionsAxi() {
      return [
        {
          label: this.i18n('dataLabels.Performance'),
          options: [
            { text: this.i18n('dataLabels.Rank'), value: 'calc.RANK' },
            { text: this.i18n('dataLabels.1 Week Performance'), value: 'd._PCTCHG_1W' },
            { text: this.i18n('dataLabels.1 Month Performance'), value: 'd._PCTCHG_1M' },
            { text: this.i18n('dataLabels.3 Month Performance'), value: 'd._PCTCHG_3M' },
            { text: this.i18n('dataLabels.6 Month Performance'), value: 'd._PCTCHG_6M' },
            { text: this.i18n('dataLabels.1 Year Performance'), value: 'd._PCTCHG_1Y' },
            { text: this.i18n('dataLabels.Year to Date Performance'), value: 'd._PCTCHG_YR' },
            { text: this.i18n('dataLabels.Year High Difference'), value: 'calc.YRHIGH_DIFF' },
            { text: this.i18n('dataLabels.Year Low Difference'), value: 'calc.YRLOW_DIFF' }
          ]
        },
        // Firm Size
        {
          label: this.i18n('dataLabels.Valuation'),
          options: [
            { text: this.i18n('dataLabels.Market Capitalization'), value: 'rkd.MKTCAP' },
            { text: this.i18n('dataLabels.Income'), value: 'rkdp.EBIT' },
            { text: this.i18n('dataLabels.Sales'), value: 'rkdp.TOTAL_REVENUE' },
            { text: this.i18n('dataLabels.Employees'), value: 'rkdp.EMPLOYEES_NUM' },
            // Growth Measures
            { text: this.i18n('dataLabels.EPS Growth this Y/Y'), value: 'rkd.EPS_CHANGE_CURRY_TO_LST1Y' },
            { text: this.i18n('dataLabels.EPS Growth next Y/Y'), value: 'rkd.EPS_CHANGE_NXT1Y_TO_CURRY' },
            { text: this.i18n('dataLabels.PEG'), value: 'rkd.PEG' },
            // Valuation measures
            { text: this.i18n('dataLabels.P/E'), value: 'rkd.PRICE_TO_EPS' },
            { text: this.i18n('dataLabels.Forward P/E'), value: 'rkd.PRICE_TO_EPS_NXT1Y' },
            { text: this.i18n('dataLabels.P/S'), value: 'rkd.PRICE_TO_NET_SALES' },
            { text: this.i18n('dataLabels.P/B'), value: 'rkd.PRICE_TO_BVPS' },
            { text: this.i18n('dataLabels.P/C'), value: 'rkd.PRICE_TO_CPS' },
            { text: this.i18n('dataLabels.P/FCF'), value: 'rkd.P2FCF_CURA_MOREY' },
            { text: this.i18n('dataLabels.Dividend Yield'), value: 'x.YIELD' },
            { text: this.i18n('dataLabels.ROE'), value: 'rkdp.RETURN_ON_EQUITY' },
            { text: this.i18n('dataLabels.ROI'), value: 'rkdp.RETURN_ON_INVESTMENT' },
            { text: this.i18n('dataLabels.ROA'), value: 'rkdp.RETURN_ON_ASSETS' },
            { text: this.i18n('dataLabels.Total Debt to Equity'), value: 'rkdp.DEBT_EQUITY_RATIO' },
            { text: this.i18n('dataLabels.Gross Margin'), value: 'rkdp.GROSS_PROFIT' },
            { text: this.i18n('dataLabels.Operating Margin'), value: 'rkdp.OPERATING_MARGIN_VALUE' }
          ]
        },
        {
          label: this.i18n('dataLabels.Volume and Volatility'),
          options: [
            { text: this.i18n('dataLabels.Turnover'), value: 'q._TURNOVER' },
            { text: this.i18n('dataLabels.Traded Volume'), value: 'q.ACVOL_1' },
            { text: this.i18n('dataLabels.Average Volume 1M'), value: 'd._AVGVOL_1M' },
            { text: this.i18n('dataLabels.Relative Volume Intraday'), value: 'calc.REL_VOL_INTRADAY' },
            { text: this.i18n('dataLabels.Relative Volume Yesterday'), value: 'calc.REL_VOL_EOD' },
            { text: this.i18n('dataLabels.Weekly Volatility'), value: 'd._VOLA_1W' },
            { text: this.i18n('dataLabels.Monthly Volatility'), value: 'd._VOLA_1M' },
            { text: this.i18n('dataLabels.Beta'), value: 'd._BETA_1Y' },
            { text: this.i18n('dataLabels.52-Week Low'), value: 'd._LOW_1Y' },
            { text: this.i18n('dataLabels.52-Week High'), value: 'd._HIGH_1Y' }
          ]
        },
        {
          label: this.i18n('dataLabels.Intraday'),
          options: [
            { text: this.i18n('dataLabels.Change'), value: 'q._PCTCHNG' },
            { text: this.i18n('dataLabels.Change from Open'), value: 'calc.PCTCHNG_OPEN' },
            { text: this.i18n('dataLabels.Opening Gap'), value: 'calc.OPEN_GAP' }
          ]
        },
        // {
        //   label: this.i18n('dataLabels.ESG Scores'),
        //   options: [
        //     { text: this.i18n('dataLabels.ESG Combined Score'), value: 'TR.TRESGCScore' },
        //     { text: this.i18n('dataLabels.ESG Score'), value: 'TR.TRESGScore' },
        //     { text: this.i18n('dataLabels.ESG Controversies Score'), value: 'TR.TRESGCControversiesScore' },
        //     { text: this.i18n('dataLabels.Environment Pillar Score'), value: 'TR.EnvironmentPillarScore' },
        //     { text: this.i18n('dataLabels.Social Pillar Score'), value: 'TR.SocialPillarScore' },
        //     { text: this.i18n('dataLabels.Governance Pillar Score'), value: 'TR.GovernancePillarScore' },
        //     { text: this.i18n('dataLabels.Resource Use Score'), value: 'TR.TRESGResourceUseScore' },
        //     { text: this.i18n('dataLabels.Emissions Score'), value: 'TR.TRESGEmissionsScore' },
        //     { text: this.i18n('dataLabels.Innovation Score'), value: 'TR.TRESGInnovationScore' },
        //     { text: this.i18n('dataLabels.Workforce Score'), value: 'TR.TRESGWorkforceScore' },
        //     { text: this.i18n('dataLabels.Human Rights Score'), value: 'TR.TRESGHumanRightsScore' },
        //     { text: this.i18n('dataLabels.Community Score'), value: 'TR.TRESGCommunityScore' },
        //     { text: this.i18n('dataLabels.Product Responsibility Score'), value: 'TR.TRESGProductResponsibilityScore' },
        //     { text: this.i18n('dataLabels.Management Score'), value: 'TR.TRESGManagementScore' },
        //     { text: this.i18n('dataLabels.Shareholders Score'), value: 'TR.TRESGShareholdersScore' },
        //     { text: this.i18n('dataLabels.CSR Strategy Score'), value: 'TR.TRESGCSRStrategyScore' },
        //     { text: this.i18n('dataLabels.ESG Reporting Scope'), value: 'TR.CSRReportingScope' }
        //   ]
        // },
        {
          label: this.i18n('dataLabels.Various'),
          options: [
            { text: this.i18n('dataLabels.Analyst Recommendation'), value: 'rkd.RECOM_MEAN_CURR' },
            { text: this.i18n('dataLabels.Target Price'), value: 'rkd4.TARGETPRICE' },
            { text: this.i18n('dataLabels.Target Price Difference'), value: 'calc.TARGETPRICE_DIFF' }
          ]
        }
      ];
    },
    optionsBubbleSize() {
      return [
        { text: this.i18n('dataLabels.Constant'), value: '' },
        { text: this.i18n('dataLabels.Market Capitalization'), value: 'rkd.MKTCAP' },
        { text: this.i18n('dataLabels.Turnover'), value: 'q._TURNOVER' },
        { text: this.i18n('dataLabels.Traded Volume'), value: 'q.ACVOL_1' },
        // #{ text: this.i18n('dataLabels.Volume USD'), value: 'Volume USD' },
        { text: this.i18n('dataLabels.Average Volume 1M'), value: 'd._AVGVOL_1M' },
        // #{ text: this.i18n('dataLabels.Average Volume USD'), value: 'Average Volume USD' },
        { text: this.i18n('dataLabels.Relative Volume Intraday'), value: 'calc.REL_VOL_INTRADAY' },
        { text: this.i18n('dataLabels.Relative Volume Yesterday'), value: 'calc.REL_VOL_EOD' }
      ];
    },
    optionsBubbleColor() {
      return [
        { text: this.i18n('dataLabels.Constant'), value: '' },
        { text: this.i18n('dataLabels.Sector'), value: 'rkd.COMP_TAXONOMY_TRBC_CD_L1' },
        { text: this.i18n('dataLabels.Industry'), value: 'rkd.COMP_TAXONOMY_TRBC_CD_L2' },
        { text: this.i18n('dataLabels.Country'), value: 'q._COUNTRY' },
        { text: this.i18n('dataLabels.Market Capitalization'), value: 'rkd.MKTCAP' },
        { text: this.i18n('dataLabels.Change'), value: 'q._PCTCHNG' },
        { text: this.i18n('dataLabels.1 Week Performance'), value: 'd._PCTCHG_1W' },
        { text: this.i18n('dataLabels.1 Month Performance'), value: 'd._PCTCHG_1M' },
        { text: this.i18n('dataLabels.3 Month Performance'), value: 'd._PCTCHG_3M' },
        { text: this.i18n('dataLabels.6 Month Performance'), value: 'd._PCTCHG_6M' },
        { text: this.i18n('dataLabels.1 Year Performance'), value: 'd._PCTCHG_1Y' },
        { text: this.i18n('dataLabels.Year to Date Performance'), value: 'd._PCTCHG_YR' },
        { text: this.i18n('dataLabels.Turnover'), value: 'q._TURNOVER' },
        { text: this.i18n('dataLabels.Traded Volume'), value: 'q.ACVOL_1' },
        // { text: this.i18n('dataLabels.Volume USD'), value: 'Volume USD' },
        { text: this.i18n('dataLabels.Average Volume 1M'), value: 'd._AVGVOL_1M' },
        // #{ text: this.i18n('dataLabels.Average Volume USD'), value: 'Average Volume USD' },
        { text: this.i18n('dataLabels.Relative Volume Intraday'), value: 'calc.REL_VOL_INTRADAY' },
        { text: this.i18n('dataLabels.Relative Volume Yesterday'), value: 'calc.REL_VOL_EOD' },
        { text: this.i18n('dataLabels.Analyst Recommendation'), value: 'rkd.RECOM_MEAN_CURR' }

        // { text: this.i18n('dataLabels.ESG Combined Score'), value: 'TR.TRESGCScore' },
        // { text: this.i18n('dataLabels.ESG Score'), value: 'TR.TRESGScore' },
        // { text: this.i18n('dataLabels.ESG Controversies Score'), value: 'TR.TRESGCControversiesScore' },
        // { text: this.i18n('dataLabels.Environment Pillar Score'), value: 'TR.EnvironmentPillarScore' },
        // { text: this.i18n('dataLabels.Social Pillar Score'), value: 'TR.SocialPillarScore' },
        // { text: this.i18n('dataLabels.Governance Pillar Score'), value: 'TR.GovernancePillarScore' },
        // { text: this.i18n('dataLabels.Resource Use Score'), value: 'TR.TRESGResourceUseScore' },
        // { text: this.i18n('dataLabels.Emissions Score'), value: 'TR.TRESGEmissionsScore' },
        // { text: this.i18n('dataLabels.Innovation Score'), value: 'TR.TRESGInnovationScore' },
        // { text: this.i18n('dataLabels.Workforce Score'), value: 'TR.TRESGWorkforceScore' },
        // { text: this.i18n('dataLabels.Human Rights Score'), value: 'TR.TRESGHumanRightsScore' },
        // { text: this.i18n('dataLabels.Community Score'), value: 'TR.TRESGCommunityScore' },
        // { text: this.i18n('dataLabels.Product Responsibility Score'), value: 'TR.TRESGProductResponsibilityScore' },
        // { text: this.i18n('dataLabels.Management Score'), value: 'TR.TRESGManagementScore' },
        // { text: this.i18n('dataLabels.Shareholders Score'), value: 'TR.TRESGShareholdersScore' },
        // { text: this.i18n('dataLabels.CSR Strategy Score'), value: 'TR.TRESGCSRStrategyScore' },
        // { text: this.i18n('dataLabels.ESG Reporting Scope'), value: 'TR.CSRReportingScope' }
      ];
    },
    optionsIndexWatchlist() {
      return [
        {
          label: 'Canadian Indices',
          options: [
            { text: 'TSX', value: '.GSPTSE' },
            { text: 'TSX 60', value: '.SPTSE' },
            { text: 'TSX Venture', value: '.SPCDNX' },
            { text: 'TSX Small Cap', value: '.SPTSES' }
          ]
        },
        {
          label: 'Canadian Sectors',
          options: [
            { text: 'CAN CONS DISCR', value: '.GSPTTCD' },
            { text: 'CAN CONS STPLS', value: '.GSPTTCS' },
            { text: 'CAN GOLD NDX', value: '.SPTTGD' },
            { text: 'CAN HLTH CARE', value: '.GSPTTHC' },
            { text: 'CAN INDUSTRIALS', value: '.GSPTTIN' },
            { text: 'CAN REAL ESTATE', value: '.GSPTTRE' },
            { text: 'CAN TECH INDEX', value: '.SPTTTK' },
            { text: 'S&P/TSX Capped Communication Service', value: '.GSPTTTS' },
            { text: 'S&P/TSX Capped Financials Index', value: '.SPTTFS' },
            { text: 'S&P/TSX Capped Materials Index', value: '.GSPTTMT' },
            { text: 'S&P/TSX ENERGY', value: '.SPTTEN' },
            { text: 'S&P/TSX UTILS', value: '.GSPTTUT' }
          ]
        },
        {
          label: 'US Indices',
          options: [
            { text: 'Dow Jones', value: '.DJI' },
            { text: 'S&P 500', value: '.SPX' },
            { text: 'Nasdaq 100', value: '.NDX' },
            // { text: 'Russell 2000', value: '.RUT' },
            { text: 'S&P 100', value: '.OEX' }
            // { text: 'NYSE Composite', value: '.NYA' }
          ]
        },
        {
          label: 'US Sectors',
          options: [
            { text: 'S&P 500 Energy', value: '.5SP10' },
            { text: 'S&P 500 Materials', value: '.5SP15' },
            { text: 'S&P 500 Industrials', value: '.5SP20' },
            { text: 'S&P 500 Cons. Discr.', value: '.5SP25' },
            { text: 'S&P 500 Cons. Staples', value: '.5SP30' },
            { text: 'S&P 500 Health Care', value: '.5SP35' },
            { text: 'S&P 500 Financials', value: '.5SP40' },
            { text: 'S&P 500 Technology', value: '.5SP45' },
            { text: 'S&P 500 Communication', value: '.5SP50' },
            { text: 'S&P 500 Utilities', value: '.5SP55' }
          ]
        }
        // . {
        //   text: 'Watchlists',
        //   value: this.$store.getters['watchlists/watchlistsSorted'].map(watchlist => ({
        //     text: watchlist.name,
        //     value: watchlist.id
        //   }))
        // }
      ];
    },
    calcFids() {
      return Object.keys(this.fidFormatters).filter(fid => fid.indexOf('calc.') === 0);
    },
    chart() {
      return this.$refs.chart.chart;
    },
    requestFids() {
      const fids = new Set();
      this.flatAxiOptions.concat(this.optionsBubbleSize, this.optionsBubbleColor).forEach(option => {
        if (!/^(calc\.|[^.]*$)/.test(option.value)) fids.add(option.value);
      });
      return [
        'q.RIC',
        'x._TICKER',
        'q._DSPLY_NAME',
        'q._TRDPRC_1',
        'q._OPEN_PRC',
        'q._HST_CLOSE',
        'q._PRE_ACVOL_1',
        ...fids
      ];
    },
    controlIndexWatchlist() {
      return this.$store.state.widget[this.wid].indexWatchlist;
    },
    controlXAxis() {
      return this.$store.state.widget[this.wid].xAxis;
    },
    selectedXAxisLabel() {
      return this.flatAxiOptions.find(option => option.value === this.controlXAxis).text;
    },
    controlYAxis() {
      return this.$store.state.widget[this.wid].yAxis;
    },
    selectedYAxisLabel() {
      return this.flatAxiOptions.find(option => option.value === this.controlYAxis).text;
    },
    controlBubbleSize() {
      return this.$store.state.widget[this.wid].bubbleSize;
    },
    selectedBubbleSizeLabel() {
      return this.optionsBubbleSize.find(option => option.value === this.controlBubbleSize).text;
    },
    controlBubbleColor() {
      return this.$store.state.widget[this.wid].bubbleColor;
    },
    selectedBubbleColorLabel() {
      return this.optionsBubbleColor.find(option => option.value === this.controlBubbleColor).text;
    },
    flatAxiOptions() {
      return this.optionsAxi.reduce((acc, curr) => {
        acc.push(...curr.options);
        return acc;
      }, []);
    },
    selectedSymbols() {
      return Object.keys(this.selectedBubbles).map(ticker => this.selectedBubbles[ticker].ric);
    }
  },
  watch: {
    fontSize() {
      // we need to reflow chart elements after font changed, but reflow doesn't happen, if the size of the chart container doesn't change. Let's kinda change it then
      this.chart.container.parentNode.style.width = this.chart.container.parentNode.offsetWidth - 2 + 'px';
      setTimeout(() => {
        this.chart.reflow();
        this.chart.container.parentNode.style.width = '';
      }, 1750);
    },
    locale: {
      handler() {
        this.configureChart();
      }
    },
    '$store.state.applicationSettings.general.settings.applicationLanguage'() {
      this.chartOptions.lang.accessibility.chartContainerLabel = this.getChartTitle();
      this.chartOptions.lang.accessibility.svgContainerLabel = this.getChartTitle();
      this.chartOptions.lang.accessibility.defaultChartTitle = this.getChartTitle();
    }
  },
  methods: {
    i18n(subkey, ns) {
      return this.$t(`white-label.${ns || 'widgets.bubbleChart'}.${subkey}`);
    },
    onIndexWatchlistSelected(val) {
      this.$store.commit(`widget/${this.wid}/INDEX_WATCHLIST_CHANGED`, val);
      this.chart.zoomOut();
      this.fetch();
    },
    onXAxisSelected(val) {
      this.$store.commit(`widget/${this.wid}/X_AXIS_CHANGED`, val);
      this.configureChart();
    },
    onYAxisSelected(val) {
      this.$store.commit(`widget/${this.wid}/Y_AXIS_CHANGED`, val);
      this.configureChart();
    },
    onBubbleSizeSelected(val) {
      this.$store.commit(`widget/${this.wid}/BUBBLE_SIZE_CHANGED`, val);
      this.configureChart();
    },
    onBubbleColorSelected(val) {
      this.$store.commit(`widget/${this.wid}/BUBBLE_COLOR_CHANGED`, val);
      this.chartOptions.series = []; // force highcharts to remove all data and not let it track series mutations
      this.selectedBubbles = {}; // reset bubbles selection as highcharts doesn't fire point deselection event on series removal
      this.$nextTick(this.configureChart);
    },
    onResize() {
      this.chart.reflow();
    },
    configureChart() {
      this.chartOptions.xAxis.title.text = this.selectedXAxisLabel;
      this.chartOptions.yAxis.title.text = this.selectedYAxisLabel;
      this.setChartSeries();
    },
    pointFormatter(point) {
      const output = [`<h2>${point.label}</h2><h5>${point.fullName}</h5>`];
      const infoRows = [
        {
          label: this.selectedXAxisLabel,
          value: this.formatFid(point.x, this.controlXAxis)
        }
      ];
      if (this.controlYAxis !== this.controlXAxis)
        infoRows.push({
          label: this.selectedYAxisLabel,
          value: this.formatFid(point.y, this.controlYAxis)
        });
      if (![this.controlXAxis, this.controlYAxis, ''].includes(this.controlBubbleSize))
        infoRows.push({
          label: this.selectedBubbleSizeLabel,
          value: this.formatFid(point.z, this.controlBubbleSize)
        });
      if (![this.controlXAxis, this.controlYAxis, this.controlBubbleSize, ''].includes(this.controlBubbleColor))
        infoRows.push({
          label: this.selectedBubbleColorLabel,
          value: this.formatFid(point.colorGroup, this.controlBubbleColor)
        });
      infoRows.forEach(infoRow => {
        output.push(`<div>${infoRow.label}: ${infoRow.value}</div>`);
      });
      return output.join('');
    },
    descriptionFormatter(point) {
      const output = [];
      output.push(point.label);
      output.push(point.fullName);
      output.push(this.selectedXAxisLabel);
      output.push(this.formatFid(point.x, this.controlXAxis));
      if (this.controlYAxis !== this.controlXAxis) {
        output.push(this.selectedYAxisLabel);
        output.push(this.formatFid(point.y, this.controlYAxis));
      }
      if (![this.controlXAxis, this.controlYAxis, ''].includes(this.controlBubbleSize)) {
        output.push(this.selectedBubbleSizeLabel);
        output.push(this.formatFid(point.z, this.controlBubbleSize));
      }
      if (![this.controlXAxis, this.controlYAxis, this.controlBubbleSize, ''].includes(this.controlBubbleColor)) {
        output.push(this.selectedBubbleColorLabel);
        output.push(this.formatFid(point.colorGroup, this.controlBubbleColor));
      }
      return output.join(' ');
    },
    // select bubbles on chart drag, disable zooming on selection
    onChartDrag(e) {
      if (e.resetSelection) return true;
      this.chart.series.forEach(series => {
        series.points.forEach(point => {
          if (
            point.x >= e.xAxis[0].min &&
            point.x <= e.xAxis[0].max &&
            point.y >= e.yAxis[0].min &&
            point.y <= e.yAxis[0].max
          ) {
            point.select(true, true);
          }
        });
      });
      return false; // Don't zoom
    },
    onChartClick() {
      this.chart.getSelectedPoints().forEach(point => point.select(false));
    },
    // react to bubbles selection
    onChartBubbleToggle(ev) {
      if (ev.type === 'select') {
        this.$set(this.selectedBubbles, ev.target.label, ev.target);
        this.doLinkUpdate(ev.target.ric);
      } else {
        this.$delete(this.selectedBubbles, ev.target.label);
      }
    },
    doLinkUpdate: debounce(function(value) {
      this.sendLinkUpdate(value);
    }, 500),
    // prepare data for highcharts series format
    setChartSeries() {
      const isXRank = this.controlXAxis === 'calc.RANK';
      const isYRank = this.controlYAxis === 'calc.RANK';
      const isSizeConstant = this.controlBubbleSize === '';
      if ((isXRank && isYRank) || !this.currentData.length) {
        this.chartOptions.series = [];
        return;
      }
      const seriesData = this.currentData.reduce((acc, entity) => {
        // TODO: decide what to do with entities with incomplete data. Current: do not plot bubbles for them
        // console.warn(entity['x._TICKER'], entity[this.controlYAxis]);
        if (
          (!isXRank && isNaN(entity[this.controlXAxis])) ||
          (!isYRank && isNaN(entity[this.controlYAxis])) ||
          (!isSizeConstant && isNaN(entity[this.controlBubbleSize]))
        )
          return acc;
        acc.push({
          id: entity['x._TICKER'], // identify the same bubble on data update and animate it to the new position/shape
          ric: entity['q.RIC'],
          label: entity['x._TICKER'],
          fullName: entity['q._DSPLY_NAME'],
          x: isXRank ? 1 : this.formatFidForChart(entity[this.controlXAxis], this.controlXAxis),
          y: isYRank ? 1 : this.formatFidForChart(entity[this.controlYAxis], this.controlYAxis),
          z: isSizeConstant ? 1 : this.formatFidForChart(entity[this.controlBubbleSize], this.controlBubbleSize),
          colorGroup: this.formatFidForChart(entity[this.controlBubbleColor], this.controlBubbleColor)
        });
        return acc;
      }, []);
      if (!seriesData.length)
        throw new Error(
          `No eligible data could be found in the response. (x: ${this.controlXAxis}; y: ${this.controlYAxis}; size: ${this.controlBubbleSize})`
        );
      // set data ranks for corresponding values on the opposite axis
      if (isXRank || isYRank) {
        const sortKey = isXRank ? 'y' : 'x';
        const rankKey = isXRank ? 'x' : 'y';
        seriesData.sort((a, b) => {
          return b[sortKey] - a[sortKey];
        });
        let rank = 1;
        let lastVal = seriesData[0][sortKey];
        seriesData.forEach(entity => {
          if (entity[sortKey] !== lastVal) {
            lastVal = entity[sortKey];
            rank++;
          }
          entity[rankKey] = rank;
        });
      }
      this.chartOptions.xAxis.allowDecimals = !isXRank;
      this.chartOptions.yAxis.allowDecimals = !isYRank;
      this.chartOptions.yAxis.reversed = isYRank;
      this.chartOptions.series = this.createChartSeries(seriesData);
      this.chartOptions.legend.enabled = this.controlBubbleColor !== '';
    },
    createChartSeries(seriesData) {
      if (!this.controlBubbleColor) {
        return [{ data: seriesData }];
      }
      const series = [];
      if (
        ['rkd.MKTCAP', 'q._TURNOVER', 'q.ACVOL_1', 'd._AVGVOL_1M', 'rkd.RECOM_MEAN_CURR'].includes(
          this.controlBubbleColor
        )
      ) {
        const dataLength = seriesData.length;
        seriesData.sort((a, b) => a.colorGroup - b.colorGroup);
        let i = 0,
          groups = 10;
        while (i < dataLength) {
          const chunk = Math.ceil((dataLength - i) / groups--);
          const serieData = seriesData.slice(i, (i += chunk));
          series.push({
            name:
              this.formatFid(serieData[0].colorGroup, this.controlBubbleColor) +
              ' .. ' +
              this.formatFid(serieData.slice(-1)[0].colorGroup, this.controlBubbleColor),
            data: serieData,
            colorIndex: series.length
          });
        }
      } else if (/PCTCHN?G/.test(this.controlBubbleColor)) {
        seriesData.sort((a, b) => a.colorGroup - b.colorGroup);
        const minusData = seriesData.filter(entity => entity.colorGroup < 0);
        const plusData = seriesData.filter(entity => entity.colorGroup >= 0);
        const minusGroupsCount = Math.round((minusData.length / seriesData.length) * this.maxChangeColorGroups);
        const plusGroupsCount = Math.max(plusData.length ? 1 : 0, this.maxChangeColorGroups - minusGroupsCount);
        // calculate value groups and colorIndex (mapped to $chartNegativeColor and $chartPositiveColors - see css file)
        [-1, 1].forEach(dir => {
          const data = dir < 0 ? minusData : plusData;
          const dataCount = dir < 0 ? minusGroupsCount : plusGroupsCount;
          const min = dir > 0 && minusGroupsCount ? 0 : Math.floor(data[0].colorGroup * 10) / 10;
          const max = dir < 0 && plusGroupsCount ? 0 : Math.ceil(data.slice(-1)[0].colorGroup * 10) / 10;
          const step = (max - min) / dataCount;
          let isFirstSeries = true;
          let group = 0;
          data.forEach(point => {
            let newGroup = isFirstSeries;
            while (Number(point.colorGroup) > min + step * (group + 1)) {
              group++;
              newGroup = true;
            }
            if (newGroup) {
              const groupFrom = min + step * group;
              const groupTo = min + step * group + step;
              series.push({
                name: Math.round(groupFrom * 10) / 10 + ' .. ' + Math.round(groupTo * 10) / 10,
                data: [point],
                colorIndex: (dir < 0 ? 200 : 300) + Math.round(group * (this.maxChangeColorGroups / dataCount))
              });
              isFirstSeries = false;
            } else {
              series[series.length - 1].data.push(point);
            }
          });
        });
      } else if (['calc.REL_VOL_INTRADAY', 'calc.REL_VOL_EOD'].includes(this.controlBubbleColor)) {
        seriesData.forEach(point => {
          const group = Math.min(Math.floor(point.colorGroup / 10), 10);
          if (!series[group]) {
            series[group] = {
              name: (group < 10 ? group * 10 + ' - ' + (group * 10 + 10) : '>100') + '%',
              data: [point],
              colorIndex: 100 + group // we could actually name the index whatever we want, but highcharts doesn't update tooltop class properly if colorIndex is not a number
            };
          } else {
            series[group].data.push(point);
          }
        });
      } else if (
        ['rkd.COMP_TAXONOMY_TRBC_CD_L1', 'rkd.COMP_TAXONOMY_TRBC_CD_L2', 'q._COUNTRY'].includes(this.controlBubbleColor)
      ) {
        seriesData.forEach(point => {
          const seriesIndex = series.findIndex(serie => serie.id === point.colorGroup);
          if (seriesIndex < 0) {
            series.push({
              id: point.colorGroup,
              name: this.formatFid(point.colorGroup, this.controlBubbleColor),
              data: [point]
            });
          } else {
            series[seriesIndex].data.push(point);
          }
        });
      }
      return series;
    },
    formatAxisLabel(label) {
      const fid = this[`control${label.axis.coll[0].toUpperCase()}Axis`];
      let val = label.axis.defaultLabelFormatter.call(label);
      if (this.fidFormatters[fid].includes('percentage')) val += ' %';
      return val;
    },
    formatFid(val, fid) {
      switch (fid) {
        case 'rkd.MKTCAP':
        case 'rkdp.EBIT':
        case 'rkdp.TOTAL_REVENUE':
        case 'q._TURNOVER':
        case 'q.ACVOL_1': {
          val /= 1000;
          let suffix = 'thousands';
          if (val >= 1000) {
            if (val < 1000000) {
              suffix = 'millions';
              val /= 1000;
            } else {
              suffix = 'billions';
              val /= 1000000;
            }
          }
          val =
            this.$f(Math.round(val * 100) / 100, this.fidFormatters[fid]) +
            ' ' +
            this.i18n(suffix, 'globalLabels.short');
          break;
        }
        case 'rkd.RECOM_MEAN_CURR':
          val = this.$f(Math.round(val * 100) / 100, this.fidFormatters[fid]);
          break;
        case 'rkd.COMP_TAXONOMY_TRBC_CD_L1':
          val = this.$t('white-label.sectors.' + val);
          break;
        case 'rkd.COMP_TAXONOMY_TRBC_CD_L2':
          val = this.$t('white-label.industries.' + val);
          break;
        default:
          val = this.$f(val, this.fidFormatters[fid]);
      }

      return val;
    },
    formatFidForChart(val, fid) {
      switch (fid) {
        case 'rkd.MKTCAP':
        case 'rkdp.EBIT':
        case 'rkdp.TOTAL_REVENUE':
          val *= 1000000;
          break;
        default:
          if (!Number.isNaN(Number(val))) val = Number(val);
      }
      return val;
    },
    fetch() {
      this.isLoading = true;
      this.chartOptions.series = [];
      this.selectedBubbles = {};
      this.fetchData()
        .then(this.aggregateData)
        .catch(err => {
          this.isLoading = false;
          this.isError = true;
          console.error(err);
        });
    },
    async fetchData() {
      let url;

      if (this.controlIndexWatchlist.startsWith('.'))
        url = '/index/constituents?ric=' + getConsolidatedRic(this.controlIndexWatchlist);
      else if (this.controlIndexWatchlist.includes('portfolio.'))
        url = '/quote/info?rics=' + this.portfolioMap[this.controlIndexWatchlist.replace('portfolio.', '')];
      // assume it's a watchlist if we have an index which doesn't start with a dot or other species handled above
      else {
        const rics = this.$store.getters['watchlists/getWatchlist'](this.controlIndexWatchlist).items;
        const constituentsReqs = [];
        const stocks = new Set();
        rics.forEach(ric => {
          if (/^(0#)?\./.test(ric))
            constituentsReqs.push(
              API.Rest.get('/index/constituents?ric=' + getConsolidatedRic(ric.replace('0#', '')) + '&fids=q.RIC')
            );
          else stocks.add(ric);
        });
        if (constituentsReqs.length) {
          await Promise.all(constituentsReqs).then(responses =>
            responses.forEach(response => {
              if (response.data && response.data.data)
                response.data.data.forEach(entity => stocks.add(entity['q.RIC']));
            })
          );
        }
        url = '/quote/info?rics=' + [...stocks.values()];
      }
      return Promise.all([
        API.Rest.get(url + '&fids=' + this.requestFids).then(response => {
          if (response.data.data.length === 0) throw new Error('No constituents received');
          if (!this.fidFormatters['x._TICKER']) {
            Object.assign(
              this.fidFormatters,
              Object.keys(response.data.metadata).reduce((acc, fid) => {
                acc[fid] = response.data.metadata[fid].type;
                return acc;
              }, {})
            );
          }
          this.currentData = response.data.data;
        }),
        this.sectorsMap
          ? Promise.resolve()
          : API.Rest.get(`/text/captiondomains?language=en&domains=TRBC&captionType=SHORT`).then(response => {
              this.sectorsMap = response.data.data[0].TRBC.SHORT;
            })
      ]);
    },
    aggregateData() {
      this.currentData.forEach(entity => {
        this.calcFids.forEach(fid => {
          switch (fid) {
            case 'calc.YRHIGH_DIFF':
              entity[fid] = (entity['q._TRDPRC_1'] / entity['d._HIGH_1Y'] - 1) * 100;
              break;
            case 'calc.YRLOW_DIFF':
              entity[fid] = (entity['q._TRDPRC_1'] / entity['d._LOW_1Y'] - 1) * 100;
              break;
            case 'calc.TARGETPRICE_DIFF':
              entity[fid] = (entity['q._TRDPRC_1'] / entity['rkd4.TARGETPRICE'] - 1) * 100;
              break;
            case 'calc.REL_VOL_INTRADAY':
              entity[fid] = (entity['q.ACVOL_1'] / entity['d._AVGVOL_1M']) * 100;
              break;
            case 'calc.REL_VOL_EOD':
              entity[fid] = (entity['q._PRE_ACVOL_1'] / entity['d._AVGVOL_1M']) * 100;
              break;
            case 'calc.PCTCHNG_OPEN':
              entity[fid] = (entity['q._TRDPRC_1'] / entity['q._OPEN_PRC'] - 1) * 100;
              break;
            case 'calc.OPEN_GAP':
              entity[fid] = (entity['q._OPEN_PRC'] / entity['q._HST_CLOSE'] - 1) * 100;
              break;
          }
        });
      });
      this.setChartSeries();
      this.isLoading = false;
    },
    getChartTitle() {
      return this.wid
        ? `${this.$t(`white-label.widgets.bubbleChart.widget-title`)} ${this.$t(
            'white-label.globalLabels.chartGraphImage'
          )}`
        : '';
    }
  },
  // lifecycle
  created() {
    const vm = this;
    this.chartOptions.xAxis.labels.formatter = this.chartOptions.yAxis.labels.formatter = this.formatAxisLabel;
    this.chartOptions.chart.events.selection = this.onChartDrag;
    this.chartOptions.chart.events.click = this.onChartClick;
    this.chartOptions.plotOptions.series.point.events.select = this.onChartBubbleToggle;
    this.chartOptions.plotOptions.series.point.events.unselect = this.onChartBubbleToggle;
    this.chartOptions.tooltip.pointFormatter = function() {
      return vm.pointFormatter(this);
    };
  },
  mounted() {
    this.configureChart();
    this.fetch();
    this.chartOptions.accessibility.point.descriptionFormatter = this.descriptionFormatter;
  },
  updated() {
    if (this.chart.xAxis[0].min && this.chartOptions.xAxis.accessibility) {
      this.chartOptions.xAxis.accessibility.rangeDescription =
        'Range:' +
        this.$f(this.chart.xAxis[0].min, 'float.price') +
        ' to ' +
        this.$f(this.chart.xAxis[0].max, 'float.price');
    }

    if (this.chart.yAxis[0].min && this.chartOptions.yAxis.accessibility) {
      this.chartOptions.yAxis.accessibility.rangeDescription =
        'Range:' +
        this.$f(this.chart.yAxis[0].min, 'float.price') +
        ' to ' +
        this.$f(this.chart.yAxis[0].max, 'float.price');
    }
  }
};
</script>

<style lang="scss">
@import '~@/styles/components/base/v-stock-chart';
@import '~@/styles/widgets/bubble-chart-widget';
</style>
